import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { DatePicker } from '../ui/date-picker'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Copy, Loader2, ArrowRight, Calendar, AlertTriangle, ChevronDown, ChevronUp, Users, X } from 'lucide-react'
import EventEndpoints from '../../endpoints/EventEndpoints'
import ScheduleEndpoints from '../../endpoints/ScheduleEndpoints'
import { SimpleEvent, IdentityRole } from '../../model/Event'
import { EventSchedule, Shift } from '../../model/EventSchedule'
import { DateTime } from 'luxon'
import { toast } from 'sonner'

interface DuplicateEventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: SimpleEvent | null
}

const formatTime = (timeStr: string): string => {
  if (!timeStr) return '-'
  try { return DateTime.fromFormat(timeStr, 'HH:mm:ss').toFormat('h:mm a') }
  catch { return timeStr }
}

const computeDuplicateName = (originalName: string): string => {
  const incremented = originalName.replace(
    /\b(19|20)\d{2}\b/g,
    (match) => String(parseInt(match, 10) + 1)
  )
  if (incremented !== originalName) return incremented
  return `${originalName} ${new Date().getFullYear()}`
}

export const DuplicateEventDialog: React.FC<DuplicateEventDialogProps> = ({
  open,
  onOpenChange,
  event,
}) => {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Full event with schedule for preview
  const [schedule, setSchedule] = useState<EventSchedule | null>(null)
  const [loadingSchedule, setLoadingSchedule] = useState(false)

  // Role picker for staff assignment copy
  const [roles, setRoles] = useState<IdentityRole[]>([])
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])
  const [rolePickerOpen, setRolePickerOpen] = useState(false)

  useEffect(() => {
    if (open && event) {
      setName(computeDuplicateName(event.name))
      setStartDate(undefined)
      setEndDate(undefined)
      setErrors({})
      setSchedule(null)
      setRoles([])
      setSelectedRoleIds([])
      setRolePickerOpen(false)

      // Load the full schedule
      setLoadingSchedule(true)
      ScheduleEndpoints.getEventSchedule(event.id, false)
        .then((data) => {
          data.startDate = new Date(data.startDate)
          data.endDate = new Date(data.endDate)
          setSchedule(data)
        })
        .catch(() => {})
        .finally(() => setLoadingSchedule(false))

      // Load source event roles for the staff-copy picker
      setLoadingRoles(true)
      EventEndpoints.getEvent(event.id)
        .then((data) => setRoles(data.roles || []))
        .catch(() => {})
        .finally(() => setLoadingRoles(false))
    }
  }, [open, event])

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  // Compute shift preview with date offsets
  const shiftPreview = useMemo(() => {
    if (!schedule || !startDate || !event) return null

    const sourceStart = new Date(schedule.startDate).getTime()
    const newStart = startDate.getTime()
    const offsetMs = newStart - sourceStart
    const newEnd = endDate ? endDate.getTime() : Infinity

    const previews: {
      shift: Shift
      oldDates: string[]
      newDates: string[]
      skipped: number
    }[] = []

    schedule.shifts?.forEach((shift) => {
      const oldDates: string[] = []
      const newDates: string[] = []
      let skipped = 0

      const sortedRecurrences = [...(shift.recurrences || [])].sort((a, b) => a.date.localeCompare(b.date))

      sortedRecurrences.forEach((rec) => {
        const oldDate = DateTime.fromISO(rec.date)
        const newDateMs = new Date(rec.date).getTime() + offsetMs
        const newDate = DateTime.fromMillis(newDateMs)

        oldDates.push(oldDate.toFormat('EEE MMM d'))

        if (newDateMs >= newStart && newDateMs <= newEnd) {
          newDates.push(newDate.toFormat('EEE MMM d'))
        } else {
          skipped++
        }
      })

      if (oldDates.length > 0) {
        previews.push({ shift, oldDates, newDates, skipped })
      }
    })

    return previews
  }, [schedule, startDate, endDate, event])

  const totalShifts = shiftPreview?.length || 0
  const totalSkipped = shiftPreview?.reduce((sum, p) => sum + p.skipped, 0) || 0

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = 'Event name is required'
    if (!startDate) e.startDate = 'Start date is required'
    if (!endDate) e.endDate = 'End date is required'
    if (startDate && endDate && endDate < startDate) e.endDate = 'End date must be after start date'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async () => {
    if (!validate() || !event) return

    setSaving(true)
    try {
      const newEvent = await EventEndpoints.duplicateEvent(event.id, {
        name: name.trim(),
        startDate: startDate!.getTime(),
        endDate: endDate!.getTime(),
        copyProvisioningsForRoleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
      })
      toast.success(`Event "${name.trim()}" created`)
      onOpenChange(false)
      navigate(`/event/${newEvent.id}/config`)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to duplicate event'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (!event) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicate Event
          </DialogTitle>
          <DialogDescription>
            Create a copy of "{event.name}" with all configuration and schedule structure.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>New Event Name *</Label>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })) }}
              disabled={saving}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>New Start Date *</Label>
              <DatePicker
                date={startDate}
                onDateChange={(d) => { setStartDate(d); if (errors.startDate) setErrors((p) => ({ ...p, startDate: '' })) }}
                placeholder="Start date"
                disabled={saving}
                className={errors.startDate ? 'border-red-500' : ''}
              />
              {errors.startDate && <p className="text-xs text-red-500">{errors.startDate}</p>}
            </div>
            <div className="space-y-2">
              <Label>New End Date *</Label>
              <DatePicker
                date={endDate}
                onDateChange={(d) => { setEndDate(d); if (errors.endDate) setErrors((p) => ({ ...p, endDate: '' })) }}
                placeholder="End date"
                disabled={saving}
                className={errors.endDate ? 'border-red-500' : ''}
              />
              {errors.endDate && <p className="text-xs text-red-500">{errors.endDate}</p>}
            </div>
          </div>

          {/* Copy staff assignments (optional) */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setRolePickerOpen((p) => !p)}
              disabled={saving}
              className="w-full flex items-center justify-between text-sm font-medium px-3 py-2 border rounded-lg hover:bg-muted/40 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Copy staff assignments from specific roles (optional)
                {selectedRoleIds.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {selectedRoleIds.length}
                  </Badge>
                )}
              </span>
              {rolePickerOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {rolePickerOpen && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">
                    Select roles whose provisioned staff should be copied to the new event
                  </Label>
                  <div className="flex gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setSelectedRoleIds(roles.map((r) => r.id))}
                      disabled={saving || loadingRoles || roles.length === 0 || selectedRoleIds.length === roles.length}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setSelectedRoleIds([])}
                      disabled={saving || selectedRoleIds.length === 0}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                {loadingRoles ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg bg-muted/20 min-h-[44px]">
                    {roles.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">No roles in this event</p>
                    ) : (
                      [...roles]
                        .sort((a, b) => a.internalName.localeCompare(b.internalName))
                        .map((role) => {
                          const isSelected = selectedRoleIds.includes(role.id)
                          return (
                            <Badge
                              key={role.id}
                              variant={isSelected ? 'default' : 'outline'}
                              className="cursor-pointer select-none"
                              onClick={() => !saving && toggleRole(role.id)}
                            >
                              {role.internalName}
                              {isSelected && <X className="h-3 w-3 ml-1" />}
                            </Badge>
                          )
                        })
                    )}
                  </div>
                )}
                {selectedRoleIds.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Staff provisioned to {selectedRoleIds.length} of {roles.length} role{roles.length !== 1 ? 's' : ''} will be copied with their accreditation level and secondary role name.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Shift Preview */}
          {startDate && schedule && shiftPreview && shiftPreview.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Schedule Preview
                </Label>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{totalShifts} shift{totalShifts !== 1 ? 's' : ''}</span>
                  {totalSkipped > 0 && (
                    <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {totalSkipped} recurrence{totalSkipped !== 1 ? 's' : ''} outside range
                    </Badge>
                  )}
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden max-h-[240px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-xs">Shift</TableHead>
                      <TableHead className="text-xs">Original Dates</TableHead>
                      <TableHead className="text-xs w-[30px]" />
                      <TableHead className="text-xs">New Dates</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shiftPreview.map((preview) => (
                      <TableRow key={preview.shift.id}>
                        <TableCell className="text-xs">
                          <div>
                            <p className="font-medium">
                              {formatTime(preview.shift.beginTime)} - {formatTime(preview.shift.endTime)}
                            </p>
                            <p className="text-muted-foreground">
                              {preview.shift.location?.name} / {preview.shift.position?.name}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {preview.oldDates.slice(0, 3).join(', ')}
                          {preview.oldDates.length > 3 && ` +${preview.oldDates.length - 3} more`}
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                        </TableCell>
                        <TableCell className="text-xs">
                          {preview.newDates.length > 0 ? (
                            <span className="text-green-700">
                              {preview.newDates.slice(0, 3).join(', ')}
                              {preview.newDates.length > 3 && ` +${preview.newDates.length - 3} more`}
                            </span>
                          ) : (
                            <span className="text-red-500 italic">All outside range</span>
                          )}
                          {preview.skipped > 0 && preview.newDates.length > 0 && (
                            <span className="text-amber-600 ml-1">({preview.skipped} skipped)</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {startDate && loadingSchedule && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading schedule preview...
            </div>
          )}

          {startDate && schedule && (!shiftPreview || shiftPreview.length === 0) && !loadingSchedule && (
            <p className="text-sm text-muted-foreground italic py-2">No shifts to preview — this event has no schedule.</p>
          )}

          <div className="text-xs text-muted-foreground bg-muted/30 rounded-md p-3 space-y-1">
            <p className="font-medium text-foreground">What will be duplicated:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>All roles and accreditation levels</li>
              <li>All locations and positions</li>
              <li>All shifts with recurrence dates adjusted to the new date range</li>
              <li>Event settings (timezone, badge config, etc.)</li>
              {selectedRoleIds.length > 0 && (
                <li>
                  Staff provisioned to {selectedRoleIds.length} selected role{selectedRoleIds.length !== 1 ? 's' : ''} (with accreditation and secondary role name)
                </li>
              )}
            </ul>
            <p className="pt-1">
              {selectedRoleIds.length > 0
                ? <>Punch clock data is <strong>not</strong> copied.</>
                : <>Identity provisionings and punch clock data are <strong>not</strong> copied.</>}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Duplicating...
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate Event
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
